import { generalHelpers } from '@tweddle-se-team/common/helpers'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'
import { loggedInUserActions, loggedInUserSelector } from '@/store/loggedInUser'
import loggedInUserHelpers from '@/store/helpers/loggedInUserHelpers'
import { useEffect, useState, useRef, useMemo } from 'react'
import { configSelectors } from '@/store/configs'

const isServer = generalHelpers.isServer()

// Next Proxy
// This should only be used Client side, but checking if it's server to avoid issues in SSR
export const proxyInstance = axios.create({
  baseURL: isServer ? '/' : '/api',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
})

/**
 * This hooks provides a simple interface to make API calls to the backend.
 * It takes into account the current user's session stored in Redux,
 * which contains a short lived JWT token for authentication
 * @returns
 */
const useApi = () => {
  const [isInterceptorAttached, setIsInterceptorAttached] = useState(false)
  const apiInstance = useRef(null)
  const dispatch = useDispatch()
  const session = useSelector(loggedInUserSelector.selectSession)
  const apiURLClient = useSelector(configSelectors.selectApiUrlClient)

  if (!apiInstance.current && apiURLClient) {
    apiInstance.current = axios.create({
      baseURL: apiURLClient,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    apiInstance.current.interceptors.response.use(res => res.data)
  }

  useEffect(() => {
    if (!isInterceptorAttached && apiInstance.current) {
      setIsInterceptorAttached(true)

      apiInstance.current.interceptors.response.use(
        response => response,
        async error => {
          const originalRequest = error.config

          // Logic to refresh token in case it expired
          // Only if the request is not already retried
          console.log('Error in API call:', error)
          console.log('originalRequest,_retry:', originalRequest?._retry)
          if (error.response?.status === 401) {
            try {
              if (originalRequest._retry) {
                throw Error('Token refresh failed, already retried')
              }
              originalRequest._retry = true
              // Get new token from API proxy using the refresh endpoint
              // with the request cookie (withCredentials: true)
              // Important: This request is made to the Next.js API proxy
              const { data } = await proxyInstance.post('auth/refresh')
              console.log('Token refreshed successfully')
              const { jwt, user } = data
              // Update the session in Redux store
              loggedInUserHelpers.setUserData(dispatch)(user, jwt)
              // Update the Authorization header for future requests for the API axios instance
              apiInstance.current.defaults.headers.common.Authorization = `Bearer ${jwt}`
              // Set the new token in the original request and retry the request
              originalRequest.headers.Authorization = `Bearer ${jwt}`
              return apiInstance.current(originalRequest)
            } catch (refreshError) {
              // If not possible to refresh the token, log out the user
              console.error('Error refreshing token, login out:', refreshError)
              dispatch(loggedInUserActions.RESET())
              await proxyInstance.post('/auth/logout')
              return window.location.replace('/login')
            }
          }

          return Promise.reject(error)
        }
      )
    }
  }, [dispatch, isInterceptorAttached])

  useEffect(() => {
    apiInstance.current.defaults.headers.common.Authorization = session?.token ? `Bearer ${session.token}` : ''
  }, [session.token])

  const api = useMemo(
    () => ({
      get: (url, config) => apiInstance.current?.get(url, config),
      post: (url, data, config) => apiInstance.current?.post(url, data, config),
      put: (url, data, config) => apiInstance.current?.put(url, data, config),
      del: (url, data, config) => apiInstance.current?.delete(url, data, config),
      patch: (url, data, config) => apiInstance.current?.patch(url, data, config),
      getBaseURL: () => apiURLClient
    }),
    [apiInstance, apiURLClient]
  )

  return api
}

export default useApi
